import { abbreviateNumber } from "@/lib/utils/prettyName";
import useFeedContentManagementStore from "@/store/news-feed/feed-content-management/feed-content-management.slice";
import { useMemo } from "react";
import type {
  FeedVolumeGraphToolTipBasicFooterProps,
  FeedVolumeGraphToolTipListFooterProps,
} from "./NewsFeedVolumeGraph.types";
import {
  calculateCoveragePercentage,
  getFormatterForPeriod,
} from "./feedVolumeGraphHelpers";

export const BasicToolTipFooter = ({
  selectedStackByOptionValue,
  period,
  startDate,
  endDate,
  stackedBy,
  totalArticles,
  volume,
}: FeedVolumeGraphToolTipBasicFooterProps) => {
  const filteredCount =
    useFeedContentManagementStore((state) => state.pagination.totalCount) ?? 0;

  const coveragePercentage = useMemo(
    () =>
      calculateCoveragePercentage(
        stackedBy,
        filteredCount,
        totalArticles,
        volume,
      ),
    [stackedBy, totalArticles, volume, filteredCount],
  );

  const generateCoverageText = useMemo(() => {
    const text = selectedStackByOptionValue
      ? getFormatterForPeriod(period, {
          startDate: startDate,
          endDate: endDate,
        })
      : "total";
    return `of ${text} coverage`;
  }, [selectedStackByOptionValue, period, startDate, endDate]);

  return (
    <div
      key="basic-feed-volume-tooltip-footer"
      className={"flex flex-col text-gray-700"}
    >
      <div className="flex flex-row items-center gap-1 text-xs">
        <span className="font-semibold">{`${abbreviateNumber(totalArticles)}`}</span>
        <span>articles</span>
      </div>
      <div className="flex flex-row items-center gap-1 text-xs">
        <span className="font-semibold">{`${coveragePercentage}%`}</span>
        <span>{generateCoverageText}</span>
      </div>
    </div>
  );
};

/**
 * @lintignore will used with NC in future
 */
export const ListToolTipFooter = ({
  volume,
  sentiment,
  socialEngagement,
  readership,
  grossImpact,
  netImpact,
  totalArticles,
  stackedBy,
}: FeedVolumeGraphToolTipListFooterProps) => {
  const filteredCount =
    useFeedContentManagementStore((state) => state.pagination.totalCount) ?? 0;

  const coveragePercentage = useMemo(
    () =>
      calculateCoveragePercentage(
        stackedBy,
        filteredCount,
        totalArticles,
        volume,
      ),
    [stackedBy, totalArticles, volume, filteredCount],
  );

  const getColorClassName = (value: string) => {
    if (value === "Positive" || value === "POSITIVE") return "text-green-500";
    if (value === "Negative" || value === "NEGATIVE") return "text-red-500";
    return "text-gray-500";
  };
  return (
    <div
      key="list-feed-volume-tooltip-footer"
      className={"flex flex-col text-gray-700"}
    >
      <ul>
        <li>
          {/* # of Articles */}
          <div className="flex flex-row items-center gap-1 text-xs">
            <span className="flex-1"># of Articles</span>
            <span className="float-end font-semibold">{`${abbreviateNumber(totalArticles)}`}</span>
          </div>
          {/* Gross Impact */}
          <div className="flex flex-row items-center gap-1 text-xs">
            <span className="flex-1">Gross Impact</span>
            <span className="float-end font-semibold">{grossImpact}</span>
          </div>
          {/* Net Impact */}
          <div className="flex flex-row items-center gap-1 text-xs">
            <span className="flex-1">Net Impact</span>
            <span className="float-end font-semibold">{netImpact}</span>
          </div>
          {/* Net Sentiment */}
          <div className="flex flex-row items-center gap-1 text-xs">
            <span className="flex-1">Net Sentiment</span>
            <span
              className={`float-end font-semibold ${getColorClassName(sentiment)}`}
            >
              {sentiment}
            </span>
          </div>
          {/* Social Engagement */}
          <div className="flex flex-row items-center gap-1 text-xs">
            <span className="flex-1">Social Engagement</span>
            <span className="float-end font-semibold">{`${abbreviateNumber(socialEngagement)}`}</span>
          </div>
          {/* Readership */}
          <div className="flex flex-row items-center gap-1 text-xs">
            <span className="flex-1">Readership</span>
            <span className="float-end font-semibold">{`${abbreviateNumber(readership)}`}</span>
          </div>
          {/* % of Total */}
          <div className="flex flex-row items-center gap-1 text-xs">
            <span className="flex-1">% of Total</span>
            <span className="float-end font-semibold">{`${coveragePercentage}%`}</span>
          </div>
        </li>
      </ul>
    </div>
  );
};
